import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Menu, Edit2, Palette, Printer, FileDown, FileText, Undo, Redo, Share2, Loader2 } from 'lucide-react';
import ConnectionStatus from '../../../components/system/ConnectionStatus';
import { usePreviewContext } from '../providers/PreviewProvider';
import { useWorkspaceContext } from '../providers/WorkspaceProvider';

export default function WorkspaceToolbar() {
  const navigate = useNavigate();
  const { resumeId } = useParams();
  const location = useLocation();

  const {
    resumeData,
    canUndo, canRedo, handleUndo, handleRedo,
    effectiveZoom, setManualZoom, resumeAreaRef,
    handleDownload,
    handleDownloadDocx,
    isDownloading,
    theme,
    authToken,
    setToast,
  } = usePreviewContext();

  const [isSharing, setIsSharing] = useState(false);

  const { setShowCustomize } = useWorkspaceContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isEditor = location.pathname.endsWith('/editor');

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function handle(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  if (!resumeData) return null;

  const handleEditToggle = () => {
    const id = resumeId || 'master';
    if (isEditor) {
      navigate(`/app/resume/${id}/chat`);
    } else {
      navigate(`/app/resume/${id}/editor`);
    }
  };

  const handleEditClick = () => {
    handleEditToggle();
    setMenuOpen(false);
  };

  const handleStyleClick = () => {
    setShowCustomize(true);
    setMenuOpen(false);
  };

  const handlePrintClick = () => {
    window.print();
    setMenuOpen(false);
  };

  const handleShareClick = async () => {
    setMenuOpen(false);
    if (isSharing) return;
    setIsSharing(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      let res = await fetch('/api/vault/slug', { headers });
      let data = await res.json();
      let slug = data?.slug;
      let profileUrl = null;

      if (!slug) {
        const candidate = data?.suggested || `resume-${Date.now().toString(36)}`;
        res = await fetch('/api/vault/slug', {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: candidate }),
        });
        data = await res.json();
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || 'Could not create a share link.');
        }
        slug = data.slug;
        profileUrl = data.profile_url;
      }

      if (!profileUrl) {
        profileUrl = `${window.location.origin}/career/${slug}`;
      }
      await navigator.clipboard.writeText(profileUrl);
      setToast?.({ message: 'Share link copied to clipboard!' });
    } catch (err) {
      setToast?.({ message: `Couldn't create share link: ${err.message}` });
    } finally {
      setIsSharing(false);
    }
  };

  const handleDownloadClick = (format) => {
    if (format === 'DOCX') {
      handleDownloadDocx();
    } else {
      handleDownload();
    }
    setMenuOpen(false);
  };

  const handleZoomSelect = (val) => {
    if (val === 'fit-page') {
      setManualZoom(null);
    } else if (val === 'fit-width') {
      if (resumeAreaRef?.current) {
        const areaWidth = resumeAreaRef.current.clientWidth - 48;
        const scale = Math.min(1.2, Math.max(0.4, areaWidth / 700));
        setManualZoom(scale);
      }
    } else {
      setManualZoom(val);
    }
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    width: '100%',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    fontSize: '12.5px',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '6px',
    textAlign: 'left',
    boxSizing: 'border-box',
    transition: 'background 120ms ease',
    outline: 'none',
  };

  const dividerStyle = {
    height: '1px',
    background: 'var(--divider)',
    margin: '4px 6px',
  };
  return (
    <>
    <style>{`@keyframes wtb-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <div
      className="flex items-center select-none"
      style={{
        position: 'absolute',
        top: isMobile ? 64 : 14,
        left: 16,
        right: 16,
        zIndex: 45,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
      }}
    >
      {/* Left side: Connection Status wrapped in small glass circle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: theme === 'dark'
            ? 'rgba(15, 15, 22, 0.72)'
            : 'rgba(255, 255, 255, 0.72)',
          border: theme === 'dark'
            ? '1px solid rgba(255,255,255,0.07)'
            : '1px solid rgba(0,0,0,0.08)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: theme === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.3)'
            : '0 4px 12px rgba(0,0,0,0.05)',
          pointerEvents: 'auto',
        }}
      >
        <ConnectionStatus />
      </div>

      {/* Right side: Group of independent circular glassmorphism buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pointerEvents: 'auto',
        }}
      >


        {/* Undo button */}
        <button
          onClick={handleUndo}
          disabled={!canUndo}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            cursor: canUndo ? 'pointer' : 'not-allowed',
            background: theme === 'dark'
              ? 'rgba(15, 15, 22, 0.72)'
              : 'rgba(255, 255, 255, 0.72)',
            border: theme === 'dark'
              ? '1px solid rgba(255,255,255,0.07)'
              : '1px solid rgba(0,0,0,0.08)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            color: canUndo
              ? (theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)')
              : (theme === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'),
            outline: 'none',
            boxShadow: theme === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 4px 12px rgba(0,0,0,0.05)',
            transition: 'all 0.15s ease',
            opacity: canUndo ? 1 : 0.65,
          }}
          onMouseEnter={e => {
            if (canUndo) {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#000000';
            }
          }}
          onMouseLeave={e => {
            if (canUndo) {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(15, 15, 22, 0.72)' : 'rgba(255, 255, 255, 0.72)';
              e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)';
            }
          }}
          title="Undo"
        >
          <Undo size={15} strokeWidth={2.2} />
        </button>

        {/* Redo button */}
        <button
          onClick={handleRedo}
          disabled={!canRedo}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            cursor: canRedo ? 'pointer' : 'not-allowed',
            background: theme === 'dark'
              ? 'rgba(15, 15, 22, 0.72)'
              : 'rgba(255, 255, 255, 0.72)',
            border: theme === 'dark'
              ? '1px solid rgba(255,255,255,0.07)'
              : '1px solid rgba(0,0,0,0.08)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            color: canRedo
              ? (theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)')
              : (theme === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'),
            outline: 'none',
            boxShadow: theme === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 4px 12px rgba(0,0,0,0.05)',
            transition: 'all 0.15s ease',
            opacity: canRedo ? 1 : 0.65,
          }}
          onMouseEnter={e => {
            if (canRedo) {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#000000';
            }
          }}
          onMouseLeave={e => {
            if (canRedo) {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(15, 15, 22, 0.72)' : 'rgba(255, 255, 255, 0.72)';
              e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)';
            }
          }}
          title="Redo"
        >
          <Redo size={15} strokeWidth={2.2} />
        </button>

        {/* Menu button */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen(o => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: 'pointer',
              background: theme === 'dark'
                ? 'rgba(15, 15, 22, 0.72)'
                : 'rgba(255, 255, 255, 0.72)',
              border: theme === 'dark'
                ? '1px solid rgba(255,255,255,0.07)'
                : '1px solid rgba(0,0,0,0.08)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              outline: 'none',
              boxShadow: theme === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.3)'
                : '0 4px 12px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.color = theme === 'dark' ? '#ffffff' : '#000000';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = theme === 'dark' ? 'rgba(15, 15, 22, 0.72)' : 'rgba(255, 255, 255, 0.72)';
              e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
            }}
            title="Menu"
          >
            <Menu size={16} strokeWidth={2.2} />
          </button>

        {/* Dropdown Card */}
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          background: theme === 'dark' ? '#0f0f18' : '#ffffff',
          border: '1px solid var(--border-default)',
          borderRadius: 12,
          padding: '6px',
          zIndex: 100,
          width: 220,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
          transformOrigin: 'top right',
          opacity: menuOpen ? 1 : 0,
          transform: menuOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-8px)',
          pointerEvents: menuOpen ? 'auto' : 'none',
          transition: 'opacity 180ms ease, transform 180ms ease',
          boxSizing: 'border-box'
        }}>
          {/* Section: Mode Actions */}
          <button
            onClick={handleEditClick}
            style={rowStyle}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Edit2 size={14} color={isEditor ? '#10B981' : 'var(--text-secondary)'} />
            <span style={{ flex: 1 }}>{isEditor ? 'Done (Exit Editor)' : 'Edit Resume'}</span>
            {isEditor && <span style={{ fontSize: 9, fontWeight: 700, color: '#10B981', background: 'rgba(16, 185, 129, 0.1)', padding: '2px 6px', borderRadius: 4 }}>ACTIVE</span>}
          </button>

          <button
            onClick={handleStyleClick}
            style={rowStyle}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Palette size={14} color="var(--text-secondary)" />
            <span>Customize Style</span>
          </button>

          <div style={dividerStyle} />

          {/* Section: Zoom Adjustments */}
          <div style={{ padding: '4px 8px' }}>
            <span style={{ fontSize: 10, fontWeight: 750, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
              Zoom: {Math.round(effectiveZoom * 100)}%
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => handleZoomSelect('fit-page')}
                style={{ flex: 1, padding: '4px 6px', fontSize: 10.5, fontWeight: 600, background: 'var(--surface-1)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', borderRadius: 4, cursor: 'pointer' }}
              >
                Fit Page
              </button>
              <button
                onClick={() => handleZoomSelect('fit-width')}
                style={{ flex: 1, padding: '4px 6px', fontSize: 10.5, fontWeight: 600, background: 'var(--surface-1)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', borderRadius: 4, cursor: 'pointer' }}
              >
                Fit Width
              </button>
            </div>
            {/* Standard percentage quick links */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, gap: 4 }}>
              {[0.5, 0.75, 1.0, 1.25].map(scale => (
                <button
                  key={scale}
                  onClick={() => handleZoomSelect(scale)}
                  style={{
                    fontSize: 9.5, padding: '2px 4px', border: 'none', background: 'transparent',
                    color: effectiveZoom === scale ? 'var(--brand)' : 'var(--text-muted)',
                    fontWeight: effectiveZoom === scale ? 700 : 500, cursor: 'pointer'
                  }}
                >
                  {scale * 100}%
                </button>
              ))}
            </div>
          </div>

          <div style={dividerStyle} />

          {/* Section: Printing and Sharing — two distinct actions, not one */}
          <button
            onClick={handlePrintClick}
            style={rowStyle}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Printer size={14} color="var(--text-secondary)" />
            <span>Print</span>
          </button>

          <button
            onClick={handleShareClick}
            disabled={isSharing}
            style={{ ...rowStyle, opacity: isSharing ? 0.6 : 1, cursor: isSharing ? 'default' : 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {isSharing
              ? <Loader2 size={14} color="var(--text-secondary)" style={{ animation: 'wtb-spin 0.9s linear infinite' }} />
              : <Share2 size={14} color="var(--text-secondary)" />}
            <span>{isSharing ? 'Creating share link…' : 'Share Resume'}</span>
          </button>

          <div style={dividerStyle} />

          {/* Section: Downloads */}
          <span style={{ fontSize: 10, fontWeight: 750, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', margin: '4px 8px 6px' }}>
            Download File
          </span>

          <button
            onClick={() => handleDownloadClick('PDF')}
            disabled={isDownloading}
            style={{ ...rowStyle, opacity: isDownloading ? 0.6 : 1, cursor: isDownloading ? 'default' : 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            {isDownloading
              ? <Loader2 size={14} color="var(--text-secondary)" style={{ animation: 'wtb-spin 0.9s linear infinite' }} />
              : <FileDown size={14} color="var(--text-secondary)" />}
            <span>{isDownloading ? 'Preparing PDF…' : 'Download PDF'}</span>
          </button>

          <button
            onClick={() => handleDownloadClick('DOCX')}
            disabled={isDownloading}
            style={{ ...rowStyle, opacity: isDownloading ? 0.6 : 1, cursor: isDownloading ? 'default' : 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sb-item-hover-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <FileText size={14} color="var(--text-secondary)" />
            <span>Download Word (DOCX)</span>
          </button>
        </div>
      </div>
      </div>
    </div>
    </>
  );
}
